import React, { useEffect, useRef, useState } from 'react';
import { PlansCatalogSection, useCommercialCatalog } from './CompanyPlanPanel';
import gcentraHeaderMap from './assets/gcentra-header-routes-wide-v3.png';
import gcentraMarkCutout from './assets/gcentra-mark-clean.png';

const modules = [
  { title: 'Logística', description: 'Rutas, unidades, choferes y destinos.', stat: '01' },
  { title: 'Servicio', description: 'Pedidos, contratos y programados.', stat: '02' },
  { title: 'Monitor en tiempo real', description: 'Seguimiento operativo en vivo.', stat: '03' },
  { title: 'Reportes y liquidación', description: 'Cierres, lectura económica y control.', stat: '04' },
  { title: 'Gastos de Flota', description: 'Combustible, service y costos de unidad.', stat: '05' },
  { title: 'Stock y movimientos', description: 'Compras, trazabilidad y movimientos.', stat: '06' },
  { title: 'Guía y soporte', description: 'Tutoriales, contexto y acompañamiento.', stat: '07' },
];

const introSeenKey = 'gcentra-public-intro-seen';

const PublicLanding = ({ onGoToLogin, onGoToTrial, onGoToCommercialRequest }) => {
  const plansRef = useRef(null);
  const { catalog } = useCommercialCatalog();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [introVisible, setIntroVisible] = useState(false);
  const [introLeaving, setIntroLeaving] = useState(false);
  const [showFloatingHeader, setShowFloatingHeader] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const shouldShowIntro = !window.sessionStorage.getItem(introSeenKey);
    if (!shouldShowIntro) return undefined;

    setIntroVisible(true);
    const leaveTimer = window.setTimeout(() => setIntroLeaving(true), 2600);
    const hideTimer = window.setTimeout(() => {
      window.sessionStorage.setItem(introSeenKey, '1');
      setIntroVisible(false);
      setIntroLeaving(false);
    }, 3200);

    return () => {
      window.clearTimeout(leaveTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncFloatingHeader = () => {
      setShowFloatingHeader(window.scrollY > 140);
    };

    syncFloatingHeader();
    window.addEventListener('scroll', syncFloatingHeader, { passive: true });

    return () => window.removeEventListener('scroll', syncFloatingHeader);
  }, []);

  const handleSkipIntro = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(introSeenKey, '1');
    }
    setIntroLeaving(true);
    window.setTimeout(() => {
      setIntroVisible(false);
      setIntroLeaving(false);
    }, 280);
  };

  const handlePlansAction = (plan) => {
    if (plan?.key === 'trial') {
      onGoToTrial?.();
      return;
    }
    onGoToCommercialRequest?.(plan?.key || 'base');
  };

  const handleViewPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-100 text-slate-950 [font-family:'Aptos','Segoe_UI','Manrope',sans-serif]">
      <style>{`
        @keyframes publicIntroTrace {
          0% { stroke-dashoffset: 220; opacity: 0.12; }
          100% { stroke-dashoffset: 0; opacity: 0.9; }
        }
        @keyframes publicIntroPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(103,232,249,0.10), 0 0 42px rgba(56,189,248,0.16); }
          50% { box-shadow: 0 0 0 18px rgba(103,232,249,0.03), 0 0 78px rgba(56,189,248,0.28); }
        }
        @keyframes publicDash {
          to { stroke-dashoffset: -42; }
        }
        @keyframes publicFloat {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>

      {introVisible ? (
        <div className={`fixed inset-0 z-[120] overflow-hidden bg-[#f4f7fb] transition-opacity duration-500 ${introLeaving ? 'opacity-0' : 'opacity-100'}`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_28%,rgba(34,211,238,0.12),transparent_20%),linear-gradient(180deg,#f8fbff_0%,#eef4ff_52%,#f4f7fb_100%)]" />
          <svg className="absolute inset-0 h-full w-full opacity-60" viewBox="0 0 1440 900" fill="none" aria-hidden="true">
            <g stroke="rgba(100,116,139,0.22)" strokeLinecap="round">
              <path d="M-20 110 H320 V72 H760 V146 H1490" strokeWidth="4" />
              <path d="M-30 250 H250 V202 H618 V272 H1490" strokeWidth="2.5" />
              <path d="M-20 392 H360 V342 H860 V416 H1490" strokeWidth="3.5" />
              <path d="M80 -40 V220 H152 V432 H92 V980" strokeWidth="3" />
              <path d="M548 -40 V184 H610 V360 H544 V980" strokeWidth="2.5" />
              <path d="M1022 -40 V236 H954 V506 H1018 V980" strokeWidth="3" />
            </g>
            {[
              'M720 418 H862 V314 H964 V230',
              'M704 440 H534 V338 H428 V276',
              'M732 454 H860 V548 H1026 V632',
              'M690 470 H530 V590 H390 V706',
            ].map((path, index) => (
              <path
                key={path}
                d={path}
                stroke="rgba(14,165,233,0.92)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="220"
                strokeDashoffset="220"
                style={{ animation: `publicIntroTrace 1100ms cubic-bezier(0.22,1,0.36,1) ${index * 140}ms forwards` }}
              />
            ))}
          </svg>

          <button
            type="button"
            onClick={handleSkipIntro}
            className="absolute right-5 top-5 rounded-full border border-slate-300 bg-white/80 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 shadow-sm backdrop-blur"
          >
            Omitir
          </button>

          <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-6 text-center">
            <div
              className="relative flex h-32 w-32 items-center justify-center rounded-full border border-white bg-white/90 shadow-[0_24px_60px_rgba(15,23,42,0.12)]"
              style={{ animation: 'publicIntroPulse 2.1s ease-in-out infinite' }}
            >
              <img src={gcentraMarkCutout} alt="G-CENTRA" className="h-20 w-20 object-contain opacity-95" />
            </div>
            <p className="mt-7 text-[12px] font-black uppercase tracking-[0.38em] text-cyan-700">G-CENTRA</p>
            <p className="mt-3 text-sm font-medium text-slate-500">Preparando entorno operativo</p>
          </div>
        </div>
      ) : null}

      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_15%_10%,rgba(14,165,233,0.08),transparent_22%),radial-gradient(circle_at_82%_12%,rgba(15,23,42,0.05),transparent_26%),linear-gradient(180deg,#f8fafc_0%,#eef4ff_46%,#f3f6fb_100%)]" />

      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          showFloatingHeader ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-6 opacity-0'
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6">
          <div className="flex items-center justify-between gap-4 rounded-[1.8rem] border border-slate-200/80 bg-[#f8fbff]/92 px-4 py-3 shadow-[0_18px_36px_rgba(15,23,42,0.08)] backdrop-blur-2xl">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="group flex items-center gap-3 text-left">
              <div className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_36px_rgba(15,23,42,0.08)]">
                <img src={gcentraMarkCutout} alt="G-CENTRA" className="h-7 w-7 object-contain opacity-95" />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.28em] text-cyan-700">G-CENTRA</p>
                <p className="text-sm font-bold text-slate-900">Todo el control logístico en un solo lugar</p>
              </div>
            </button>

            <nav className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white/80 p-1 text-sm font-semibold text-slate-600 shadow-sm lg:flex">
              <button onClick={() => document.getElementById('modulos')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="rounded-full px-4 py-2 transition hover:bg-slate-950 hover:text-white">Módulos</button>
              <button onClick={handleViewPlans} className="rounded-full px-4 py-2 transition hover:bg-slate-950 hover:text-white">Planes</button>
              <button onClick={onGoToTrial} className="rounded-full border border-cyan-200 bg-cyan-50 px-4 py-2 text-cyan-800 transition hover:bg-cyan-300 hover:text-slate-950">Probar gratis</button>
              <button onClick={onGoToLogin} className="rounded-full bg-slate-950 px-4 py-2 text-white transition hover:bg-cyan-700">Ingresar</button>
            </nav>

            <div className="flex items-center gap-2 lg:hidden">
              <button onClick={onGoToTrial} className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-xs font-black text-cyan-800">Probar</button>
              <button onClick={onGoToLogin} className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-900">Ingresar</button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <section className="px-4 pb-12 pt-12 sm:px-6 lg:pb-16 lg:pt-20">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-4 py-2 text-[11px] font-black uppercase tracking-[0.24em] text-cyan-800 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-cyan-500" />
              Control, trazabilidad y operación
            </p>

            <div className="mt-8">
              <p className="text-[clamp(3rem,8vw,6.4rem)] font-black leading-[0.88] tracking-[-0.1em] text-slate-950">
                G-CENTRA
              </p>
              <h1 className="mt-5 text-[clamp(1.35rem,2.3vw,2.1rem)] font-black leading-[1.08] tracking-[-0.04em] text-slate-700">
                Todo el control logístico en un solo lugar
              </h1>
            </div>

            <p className="mt-6 max-w-3xl text-base font-medium leading-8 text-slate-600 sm:text-lg">
              Pensada para empresas que brindan servicios de logistica, desde operaciónes pequeñas hasta estructuras con múltiples viajes, mercadería y control operativo.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={onGoToTrial}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-4 text-sm font-black uppercase tracking-[0.16em] text-white shadow-[0_18px_42px_rgba(15,23,42,0.14)] transition hover:-translate-y-1 hover:bg-cyan-700"
              >
                Probar gratis
              </button>
              <button
                onClick={onGoToLogin}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-4 text-sm font-black uppercase tracking-[0.16em] text-slate-900 transition hover:-translate-y-1 hover:border-slate-300"
              >
                Ingresar
              </button>
            </div>

            <nav className="mt-10 flex flex-wrap items-center justify-center gap-2 rounded-[1.8rem] border border-slate-200 bg-white/92 p-2 shadow-[0_16px_36px_rgba(15,23,42,0.06)]">
              <button
                onClick={() => document.getElementById('modulos')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="rounded-full px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-950 hover:text-white"
              >
                Módulos
              </button>
              <button
                onClick={handleViewPlans}
                className="rounded-full px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-950 hover:text-white"
              >
                Planes
              </button>
              <button
                onClick={onGoToTrial}
                className="rounded-full border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-bold text-cyan-800 transition hover:bg-cyan-300 hover:text-slate-950"
              >
                Probar gratis
              </button>
              <button
                onClick={onGoToLogin}
                className="rounded-full bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-cyan-700"
              >
                Ingresar
              </button>
            </nav>
          </div>
        </section>

        <section className="px-4 pb-12 sm:px-6 lg:pb-14">
          <div className="mx-auto grid max-w-6xl gap-3 sm:grid-cols-3">
            {[
              ['Operación diaria', 'Rutas, viajes y estados en tiempo real'],
              ['Mercadería controlada', 'Compras, movimientos y trazabilidad'],
              ['Negocio visible', 'Reportes, validaciónes y control económico'],
            ].map(([title, detail]) => (
              <div key={title} className="rounded-[1.4rem] border border-slate-200 bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.05)]">
                <p className="text-sm font-black text-slate-950">{title}</p>
                <p className="mt-1 text-sm leading-6 text-slate-500">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 pb-12 sm:px-6 lg:pb-16">
          <div className="mx-auto max-w-7xl">
            <div className="relative">
              <div className="absolute inset-0 rounded-[2.6rem] bg-[radial-gradient(circle_at_50%_14%,rgba(255,255,255,0.84),transparent_26%),radial-gradient(circle_at_30%_72%,rgba(34,211,238,0.14),transparent_30%)]" />
              <div className="relative overflow-hidden rounded-[2.6rem] border border-slate-200 bg-white shadow-[0_28px_70px_rgba(15,23,42,0.10)]">
                <div className="pointer-events-none absolute inset-0">
                  <img src={gcentraHeaderMap} alt="" className="h-full w-full object-cover opacity-[0.18]" />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.58),rgba(248,250,252,0.92))]" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_54%,rgba(34,211,238,0.10),transparent_24%)]" />
                </div>

                <div className="relative z-10 p-6 sm:p-8">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.28em] text-cyan-700">Seguimiento central</p>
                      <h2 className="mt-2 max-w-3xl text-2xl font-black tracking-[-0.04em] text-slate-950 sm:text-[2rem]">
                        Sabé qué transporte está trabajando, qué ruta está haciendo y en qué destino se encuentra.
                      </h2>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                      Operación viva
                    </div>
                  </div>

                  <div className="mt-8 grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
                    <div className="relative min-h-[360px] overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950/[0.02]">
                      <svg className="absolute inset-0 h-full w-full opacity-90" viewBox="0 0 520 420" fill="none" aria-hidden="true">
                        <g stroke="rgba(71,85,105,0.28)" strokeLinecap="round">
                          <path d="M-20 82 H140 V42 H280 V106 H560" strokeWidth="4" />
                          <path d="M-10 160 H130 V130 H244 V174 H560" strokeWidth="2.5" />
                          <path d="M-20 252 H178 V214 H308 V274 H560" strokeWidth="3" />
                          <path d="M66 -20 V120 H102 V224 H66 V460" strokeWidth="3" />
                          <path d="M250 -20 V98 H290 V198 H252 V340 H292 V460" strokeWidth="2.5" />
                          <path d="M418 -20 V148 H390 V260 H430 V460" strokeWidth="3" />
                        </g>
                        <path d="M180 338 H236 V264 H312 V208 H382 V132" stroke="rgba(14,165,233,0.92)" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path
                          d="M180 338 H236 V264 H312 V208 H382 V132"
                          stroke="rgba(15,23,42,0.82)"
                          strokeWidth="2"
                          strokeDasharray="10 14"
                          style={{ animation: 'publicDash 2.8s linear infinite' }}
                        />
                      </svg>

                      <div className="absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2">
                        <div
                          className="relative flex h-28 w-28 items-center justify-center rounded-full border border-white bg-white shadow-[0_18px_46px_rgba(15,23,42,0.14)]"
                          style={{ animation: 'publicFloat 2.8s ease-in-out infinite' }}
                        >
                          <img src={gcentraMarkCutout} alt="G-CENTRA" className="h-16 w-16 object-contain opacity-95" />
                        </div>
                      </div>

                      {[
                        ['GPS', 'top-10 right-10'],
                        ['Ruta', 'bottom-14 left-10'],
                        ['Stock', 'top-1/2 right-8'],
                      ].map(([label, position]) => (
                        <div key={label} className={`absolute ${position}`}>
                          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-cyan-200 bg-white text-[11px] font-black uppercase text-cyan-800 shadow-sm" style={{ animation: 'publicIntroPulse 2.6s ease-in-out infinite' }}>
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col gap-4">
                      <div className="rounded-[1.6rem] border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-700">Seguimiento en tiempo real</p>
                        <p className="mt-3 text-sm leading-7 text-slate-600">
                          La operación responde sobre la marcha y muestra el estado de cada unidad, ruta y destino sin perder contexto.
                        </p>
                      </div>

                      <div className="rounded-[1.6rem] border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-700">Operación y mercadería en una sola plataforma</p>
                        <p className="mt-3 text-sm leading-7 text-slate-600">
                          Rutas, viajes, mercadería y control operativo trabajan sobre la misma información para evitar cortes, doble carga y pérdida de contexto.
                        </p>
                      </div>

                      <div className="rounded-[1.6rem] border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-700">Trazabilidad y control</p>
                        <p className="mt-3 text-sm leading-7 text-slate-600">
                          Cada movimiento queda registrado para entender que paso, donde paso y como impacto en la operación, la mercadería o el resultado económico.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="modulos" className="px-4 py-14 sm:px-6 lg:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-5 lg:grid-cols-[0.52fr_0.48fr] lg:items-end">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.28em] text-cyan-700">Módulos</p>
                <h2 className="mt-3 text-3xl font-black tracking-[-0.045em] text-slate-950 sm:text-[2.5rem]">
                  Estructura clara para crecer por etapas.
                </h2>
              </div>
              <p className="max-w-xl text-sm font-medium leading-7 text-slate-500 lg:ml-auto">
                G-CENTRA organiza pedido, ruta, ejecución, validación, mercadería y resultado económico dentro de una sola plataforma.
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {modules.map((module) => (
                <article key={module.title} className="group relative overflow-hidden rounded-[1.9rem] border border-slate-200 bg-white p-5 shadow-[0_16px_42px_rgba(15,23,42,0.05)] transition hover:-translate-y-1 hover:border-cyan-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="h-10 w-10 rounded-2xl border border-cyan-100 bg-cyan-50" />
                    <span className="text-4xl font-black tracking-[-0.12em] text-slate-100">{module.stat}</span>
                  </div>
                  <h3 className="mt-6 text-lg font-black tracking-tight text-slate-950">{module.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-500">{module.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section ref={plansRef} className="px-4 py-10 sm:px-6 lg:py-12">
          <div className="mx-auto max-w-7xl">
            <div className="mb-5 grid gap-4 lg:grid-cols-[0.56fr_0.44fr] lg:items-end">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.28em] text-cyan-700">Planes</p>
                <h2 className="mt-3 text-3xl font-black tracking-[-0.045em] text-slate-950 sm:text-[2.35rem]">
                  Un ingreso simple. Un crecimiento ordenado.
                </h2>
              </div>
              <p className="max-w-xl rounded-[1.35rem] border border-slate-200 bg-white p-4 text-sm font-medium leading-6 text-slate-500 shadow-sm">
                Empezá con una prueba guiada y elegí el nivel de control que hoy necesita tu empresa: desde una operación chica hasta un esquema con muchos viajes, mercadería y más seguimiento.
              </p>
            </div>

            <PlansCatalogSection
              catalog={catalog}
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
              mode="public"
              onPlanAction={handlePlansAction}
              className="mt-2"
            />
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-slate-200 bg-white px-4 py-10 text-slate-500 sm:px-6">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-black tracking-[0.2em] text-slate-950">G-CENTRA</p>
            <p className="mt-1 text-sm text-slate-500">Plataforma operativa con trazabilidad, control y lectura ejecutiva.</p>
          </div>
          <p className="text-sm text-slate-400">© 2026 G-CENTRA</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLanding;
